import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrders, updateOrderStatus } from "../../store/thunk/checkout";
import { isTokenExpired, loadStorage } from "../../Utils/Utils";
import { logOut, raiseError } from "../../store/actions/action";
import { useNavigate } from "react-router-dom";

const STATUS_OPTIONS = ["Pending", "Processing", "Shipped", "Delivered", "Cancelled"];

function AllOrders(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const Orders = useSelector((store) => store.user.allOrders);
    const userDetails = useSelector((store) => store.user.data) || loadStorage("user");
    const isAdmin = userDetails?.isAdmin;

    const [statusMap, setStatusMap] = useState({});
    const [updatingId, setUpdatingId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!isTokenExpired()) {
            if (props.userId) {
                dispatch(fetchOrders({ userId: props.userId, isAdmin: props.isAdmin }));
            }
        } else {
            dispatch(logOut);
            dispatch(raiseError("Please login again"));
            navigate('/');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStatusChange = (orderId, value) => {
        setStatusMap(prev => ({ ...prev, [orderId]: value }));
    };

    const handleStatusUpdate = async (orderId) => {
        const newStatus = statusMap[orderId];
        if (!newStatus) return;
        setUpdatingId(orderId);
        await dispatch(updateOrderStatus({ orderId, status: newStatus }));
        setUpdatingId(null);
    };

    const toggleExpand = (orderId) => {
        setExpandedId(prev => prev === orderId ? null : orderId);
    };

    return (
        <div className="col-lg-8 col-md-8 col-12">
            <div className="ml-0 ml-md-4">
                <div className="d-none d-md-block">
                    <div className="row mb-md-5">
                        <div className="col">
                            <h5 className="mb-1 text-white">Manage Orders</h5>
                            <p className="mb-0 text-white small">
                                You have full control to manage your own orders.
                            </p>
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-primary btn-sm" onClick={() => navigate(-1)}><i className="ti-angle-left"></i> Go Back</button>
                        </div>
                    </div>
                </div>
                <div className="card">
                    <div className="table-responsive">
                        <table className="table mb-0">
                            <thead>
                                <tr>
                                    <th>Order</th>
                                    <th>Customer</th>
                                    <th>Phone</th>
                                    <th>Date</th>
                                    <th>Items</th>
                                    <th>Total</th>
                                    <th>Payment Mode</th>
                                    <th>Status</th>
                                    {isAdmin && <th>Action</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {Orders && Orders.length > 0 && Orders.map((order) => (
                                    <React.Fragment key={order.id}>
                                        <tr
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => toggleExpand(order.id)}
                                        >
                                            <td className="py-3">
                                                <span className="font-weight-bold">#{order.id}</span>
                                                <br />
                                                <small className="text-muted">
                                                    {expandedId === order.id ? '▲ hide' : '▼ details'}
                                                </small>
                                            </td>
                                            <td className="py-3">
                                                <span className="font-weight-bold">{order.name}</span>
                                                {isAdmin && order.customerEmail && (
                                                    <><br /><small className="text-muted">{order.customerEmail}</small></>
                                                )}
                                            </td>
                                            <td className="py-3">{order.phone}</td>
                                            <td className="py-3">
                                                {new Date(order.orderDate).toLocaleDateString('en-IN', {
                                                    day: '2-digit', month: 'short', year: 'numeric'
                                                })}
                                            </td>
                                            <td className="py-3">{order.itemCount}</td>
                                            <td className="py-3">₹{order.totalAmount}</td>
                                            <td className="py-3">
                                                {order.paymentMethod === 'COD'
                                                    ? <span className="badge bg-warning text-dark">COD</span>
                                                    : <span className="badge bg-success">Online</span>
                                                }
                                            </td>
                                            <td className="py-3" onClick={e => e.stopPropagation()}>
                                                {isAdmin ? (
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={statusMap[order.id] ?? order.orderStatus ?? "Pending"}
                                                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                                        style={{ minWidth: 120 }}
                                                    >
                                                        {STATUS_OPTIONS.map(s => (
                                                            <option key={s} value={s}>{s}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span className={`badge ${getStatusBadge(order.orderStatus)}`}>
                                                        {order.orderStatus || "Pending"}
                                                    </span>
                                                )}
                                            </td>
                                            {isAdmin && (
                                                <td className="py-3" onClick={e => e.stopPropagation()}>
                                                    <button
                                                        className="btn btn-sm btn-primary"
                                                        disabled={updatingId === order.id}
                                                        onClick={() => handleStatusUpdate(order.id)}
                                                    >
                                                        {updatingId === order.id ? "..." : "Update"}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>

                                        {/* Expandable customer + address details */}
                                        {expandedId === order.id && (
                                            <tr>
                                                <td colSpan={isAdmin ? 9 : 8} className="p-0">
                                                    <div className="bg-light px-4 py-3 border-top">
                                                        <div className="row">
                                                            <div className="col-md-6">
                                                                <p className="mb-1 font-weight-bold small text-uppercase text-muted">Customer Info</p>
                                                                <p className="mb-1"><i className="fa fa-user mr-2 text-primary"></i>{order.name}</p>
                                                                {isAdmin && order.customerEmail && (
                                                                    <p className="mb-1"><i className="fa fa-envelope mr-2 text-primary"></i>{order.customerEmail}</p>
                                                                )}
                                                                <p className="mb-1"><i className="fa fa-phone mr-2 text-primary"></i>{order.phone}</p>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <p className="mb-1 font-weight-bold small text-uppercase text-muted">Delivery Address</p>
                                                                <p className="mb-1"><i className="fa fa-map-marker mr-2 text-primary"></i>{order.address}</p>
                                                                <p className="mb-1 ml-4">{order.locality}, {order.state} - {order.pincode}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function getStatusBadge(status) {
    switch (status) {
        case "Delivered": return "bg-success";
        case "Shipped": return "bg-info text-dark";
        case "Processing": return "bg-warning text-dark";
        case "Cancelled": return "bg-danger";
        default: return "bg-secondary";
    }
}

export default AllOrders;
